/* ==========================================================================
   Search query — what the theme sends to Shopify's search
   Vanilla JS, no dependencies. Loaded on every page (layout/theme.liquid,
   defer; sections/search-template.liquid also loads it without defer, for
   its redirect). Settings in #SearchQueryConfig (snippets/search-config.liquid):
   the filter groups, the synonyms and ignored words of Configuración del
   tema → Búsqueda, the store's tags and product types and the texts of the
   header suggestions.
   - Always by fields, never Shopify's plain search (it matches the vendor,
     "Casa del Anillo", and guesses: "rodrigo" found "Cadena Rolo"). Each
     word, with and without its final "s" / "es" and with its synonyms, in
     title, product_type and tag (tier 1) or also body (tier 2, only when
     tier 1 finds nothing). The typed word also in variants.sku. Words joined
     with AND; with OR ("any") only as the last resort. Tags of the filters
     at the end:
       ((title:Aretes OR title:arete OR title:aret OR product_type:aretes …)
        AND (title:bola OR …)) AND tag:14k
     An option with other spellings in the catalog ("also" in
     filter-groups) asks for all of them: (tag:3-oros OR tag:"3 oros").
     The first entry of each word is title:<the word as typed>:
     snippets/search-term.liquid reads it back. Change both together.
   - Words that are filter options ("14k", "oro rosa", "mujer" by its
     synonyms) leave the text and become tags (not with exact).
   - Typos: correct() against the vocabulary (tags, types, synonyms and the
     words of the titles, sections/search-vocabulary.liquid, kept 24 h).
   - The header search fields (and the search page's) send the query built
     here: submit() below.
   Used by assets/search-filters.js and snippets/ajax-search-bar.liquid.
   ========================================================================== */
(function () {
  'use strict';

  if (window.SearchQuery) return;

  var FIELDS = ['title', 'product_type', 'tag'];
  var PHRASE_FIELDS = ['title', 'tag']; // synonyms of more than one word
  var OPERATORS = ['and', 'or', 'not'];
  var MAX_TERMS = 300; // 315 tested on the store
  var MAX_PHRASE = 3;  // words
  var COMPLETIONS = 5;
  var VOCAB_SECTION = 'search-vocabulary';
  var VOCAB_KEY = 'searchVocabulary';
  var VOCAB_HOURS = 24;

  var config;
  var synonyms;   // key → [phrases]
  var filterKeys; // key → [{ group, option }]
  var tagForms;   // option value → [value, other spellings]
  var tagValue;   // any spelling → option value
  var stopwords;  // key → true
  var baseVocab;  // words of tags, types and synonyms
  var titleWords = null;
  var titleRequest = null;

  /* Words ----------------------------------------------------------------- */

  // Lowercase and without accents (ñ → n): Shopify's search ignores them too
  function norm(text) {
    return String(text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }

  // Only letters, digits and inner hyphens: nothing of Shopify's search
  // syntax gets through. AND / OR / NOT go too.
  function words(text) {
    return String(text || '').split(/\s+/).map(function (word) {
      return word.replace(/[^\p{L}\p{N}-]/gu, '').replace(/^-+|-+$/g, '');
    }).filter(function (word) {
      return word && OPERATORS.indexOf(word.toLowerCase()) === -1;
    });
  }

  // The word with and without its final "s" / "es": aretes → arete, aret;
  // collar → collars, collares. Normalized.
  function forms(word) {
    var lower = norm(word);
    var list = [lower];
    if (lower.length > 4 && /es$/.test(lower)) {
      list.push(lower.slice(0, -1), lower.slice(0, -2));
    } else if (lower.length > 3 && /s$/.test(lower)) {
      list.push(lower.slice(0, -1));
    } else {
      list.push(lower + 's');
      if (!/[aeiou]$/.test(lower)) list.push(lower + 'es');
    }
    return list;
  }

  // Comparison key: singular and plural give the same one (aretes, arete →
  // aret; collares, collar → collar). Never sent.
  function stem(word) {
    var w = norm(word);
    if (w.length > 3 && /s$/.test(w)) w = w.slice(0, -1);
    if (w.length > 3 && /[^aeiou]e$/.test(w)) w = w.slice(0, -1);
    return w;
  }

  function keyOf(phrase) {
    return words(phrase).map(stem).join(' ');
  }

  /* Settings -------------------------------------------------------------- */

  function readConfig() {
    if (config) return config;
    var el = document.getElementById('SearchQueryConfig');
    try {
      config = el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      config = {};
    }
    config.url = config.url || '/search';
    config.texts = config.texts || {};

    // One group per line: "a, b, c" are the same; "a > b, c": searching a
    // also searches b and c, not the other way round
    synonyms = {};
    function add(from, to) {
      var key = keyOf(from);
      if (!key) return;
      var list = synonyms[key] || (synonyms[key] = []);
      to.forEach(function (phrase) {
        var clean = words(phrase).map(norm).join(' ');
        if (clean && keyOf(clean) !== key && list.indexOf(clean) === -1) list.push(clean);
      });
    }
    String(config.synonyms || '').split(/\r?\n/).forEach(function (line) {
      line = line.trim();
      if (!line || line.charAt(0) === '#') return;
      var sides = line.split('>');
      var left = sides[0].split(',');
      var right = sides.length > 1 ? sides.slice(1).join(',').split(',') : [];
      left.forEach(function (phrase) {
        add(phrase, left.concat(right));
      });
    });

    stopwords = {};
    String(config.stopwords || '').split(/[,\n]/).forEach(function (word) {
      var key = keyOf(word);
      if (key) stopwords[key] = true;
    });

    // The spellings of each option's tag: its value and "also"
    tagForms = {};
    tagValue = {};
    (config.groups || []).forEach(function (group) {
      group.options.forEach(function (option) {
        if (!option.val) return;
        var forms = [option.val].concat(option.also || []);
        tagForms[String(option.val).toLowerCase()] = forms;
        forms.forEach(function (form) {
          tagValue[String(form).toLowerCase()] = String(option.val).toLowerCase();
        });
      });
    });

    // Filter options by their label, short value, tag (all its spellings)
    // and synonyms
    filterKeys = {};
    (config.groups || []).forEach(function (group) {
      if (group.options.some(function (option) { return option.href; })) return;
      group.options.forEach(function (option) {
        var phrases = [option.label, option.short, option.val, String(option.val || '').replace(/-/g, ' ')]
          .concat(option.also || []);
        phrases.slice().forEach(function (phrase) {
          (synonyms[keyOf(phrase)] || []).forEach(function (alt) { phrases.push(alt); });
        });
        phrases.forEach(function (phrase) {
          var key = keyOf(phrase);
          if (!key) return;
          var list = filterKeys[key] || (filterKeys[key] = []);
          if (!list.some(function (entry) { return entry.option === option; })) {
            list.push({ group: group, option: option });
          }
        });
      });
    });

    baseVocab = [];
    var vocabText = (config.vocab || []).join(' ') + ' ' + String(config.synonyms || '');
    vocabText.split(/[\s,>|]+/).forEach(function (word) {
      addVocab(baseVocab, word);
    });
    return config;
  }

  function addVocab(list, word) {
    word = norm(word).replace(/[^a-z]/g, '');
    if (word.length >= 3 && list.indexOf(word) === -1) list.push(word);
  }

  /* Analysis: units of text and tags -------------------------------------- */

  // A unit is a word or a phrase of the text: { display: as typed, alts:
  // [normalized phrases: itself and its synonyms] }
  function unit(tokens) {
    var display = tokens.join(' ');
    var own = tokens.map(norm).join(' ');
    return { display: display, alts: [own].concat(synonyms[keyOf(own)] || []) };
  }

  // opts.exact: no filter words. Returns { units, tags: [{ group, option }] }
  function analyze(text, opts) {
    readConfig();
    opts = opts || {};
    var tokens = words(text);
    var found = []; // in text order: { tokens, matches } or { unit }
    var i = 0;
    while (i < tokens.length) {
      var taken = 0;
      for (var size = Math.min(MAX_PHRASE, tokens.length - i); size > 0 && !taken; size--) {
        var part = tokens.slice(i, i + size);
        var key = part.map(stem).join(' ');
        if (!opts.exact && filterKeys[key]) {
          found.push({ tokens: part, matches: filterKeys[key] });
          taken = size;
        } else if (size > 1 && synonyms[key]) {
          found.push({ unit: unit(part) });
          taken = size;
        } else if (size === 1) {
          found.push({ unit: unit(part) });
          taken = 1;
        }
      }
      i += taken;
    }

    // A filter word is a tag only if it names one option, and no other word
    // of the text names another option of the same group; otherwise it stays
    // text ("10k 14k", "infantil" → niño, niña, bebé)
    var byGroup = {};
    found.forEach(function (entry) {
      if (!entry.matches) return;
      var groups = entry.matches.map(function (m) { return m.group.name; });
      var single = entry.matches.length === 1;
      if (!single) return;
      var name = groups[0];
      var current = byGroup[name];
      if (current && current.option !== entry.matches[0].option) byGroup[name] = false;
      else if (current !== false) byGroup[name] = entry.matches[0];
    });

    var units = [];
    var tags = [];
    found.forEach(function (entry) {
      if (entry.unit) {
        units.push(entry.unit);
        return;
      }
      var match = entry.matches.length === 1 && byGroup[entry.matches[0].group.name];
      if (match && match.option === entry.matches[0].option) {
        if (tags.indexOf(match) === -1) tags.push(match);
      } else {
        units.push(unit(entry.tokens));
      }
    });

    // Ignored words go, as long as something else stays
    var kept = units.filter(function (u) { return !stopwords[keyOf(u.display)]; });
    if (kept.length || tags.length) units = kept;
    return { units: units, tags: tags };
  }

  /* The query -------------------------------------------------------------- */

  function quoted(phrase) {
    return phrase.indexOf(' ') > -1 ? '"' + phrase + '"' : phrase;
  }

  // A filter option in the query: tag:<value>, or, with other spellings
  // in the catalog, all of them with OR: (tag:3-oros OR tag:"3 oros")
  function tagTerm(val) {
    readConfig();
    var forms = tagForms[String(val).toLowerCase()] || [val];
    var terms = forms.map(function (form) { return 'tag:' + quoted(String(form)); });
    return terms.length > 1 ? '(' + terms.join(' OR ') + ')' : terms[0];
  }

  function unitTerms(u, tier, altCount) {
    var list = ['title:' + quoted(u.display)];
    function push(term) {
      if (list.indexOf(term) === -1 && term.toLowerCase() !== list[0].toLowerCase()) list.push(term);
    }
    u.alts.slice(0, altCount).forEach(function (alt) {
      if (alt.indexOf(' ') > -1) {
        PHRASE_FIELDS.concat(tier > 1 ? ['body'] : []).forEach(function (field) {
          push(field + ':"' + alt + '"');
        });
        return;
      }
      FIELDS.concat(tier > 1 ? ['body'] : []).forEach(function (field) {
        forms(alt).forEach(function (form) {
          push(field + ':' + form);
        });
      });
    });
    if (u.display.indexOf(' ') === -1) push('variants.sku:' + u.display.toLowerCase());
    return list;
  }

  // Last word (the field doesn't end with a space): with the words of the
  // vocabulary that start with it, if it isn't one ("are" → aretes)
  function complete(units, text) {
    var last = units[units.length - 1];
    if (!last || /\s$/.test(text) || last.display.indexOf(' ') > -1) return;
    var start = norm(last.display);
    if (start.length < 3 || /\d/.test(start)) return;
    var vocab = vocabulary();
    if (vocab.some(function (word) { return stem(word) === stem(start); })) return;
    vocab.filter(function (word) {
      return word.length > start.length && word.indexOf(start) === 0;
    }).sort(function (a, b) {
      return a.length - b.length;
    }).slice(0, COMPLETIONS).forEach(function (word) {
      if (last.alts.indexOf(word) === -1) last.alts.push(word);
    });
  }

  // opts: exact, tags (more tag values, from the filter bar), tier (1 / 2),
  // any (OR between words), complete (the last word, see complete())
  function build(text, opts) {
    opts = opts || {};
    var result = analyze(text, opts);
    if (opts.complete) complete(result.units, String(text || ''));

    var tagValues = result.tags.map(function (t) { return t.option.val; });
    (opts.tags || []).forEach(function (val) {
      if (tagValues.indexOf(val) === -1) tagValues.push(val);
    });
    var tagPart = tagValues.map(tagTerm).join(' AND ');

    var q = '';
    if (result.units.length) {
      // Fewer synonyms if the query gets too long
      var most = Math.max.apply(null, result.units.map(function (u) { return u.alts.length; }));
      var groups;
      for (var count = most; count >= 1; count--) {
        groups = result.units.map(function (u) { return unitTerms(u, opts.tier || 1, count); });
        var total = groups.reduce(function (sum, list) { return sum + list.length; }, 0);
        if (total <= MAX_TERMS) break;
      }
      q = '(' + groups.map(function (list) {
        return '(' + list.join(' OR ') + ')';
      }).join(opts.any ? ' OR ' : ' AND ') + ')';
      if (tagPart) q += ' AND ' + tagPart;
    } else {
      q = tagPart;
    }
    return { q: q, units: result.units, tags: result.tags };
  }

  function query(text, opts) {
    return build(text, opts).q;
  }

  // What a query built here has: its tags (each one back to its option's
  // value, also from a group with OR), tier and OR, and its words part as
  // it is (text: everything before the tags; completed words and synonyms
  // included; empty for a query of tags only). ours: false for any other
  // query (an old link, plain text). The tags start at ' AND tag:' or
  // ' AND (tag:' (a word's group always starts with title:)
  function parse(q) {
    readConfig();
    q = String(q || '');
    var tagsOnly = q.indexOf('tag:') === 0 || q.indexOf('(tag:') === 0;
    var ours = q.indexOf('((title:') === 0 || tagsOnly;
    var text = '';
    var tagPart = '';
    if (tagsOnly) {
      tagPart = q;
    } else if (ours) {
      var cuts = [q.indexOf(' AND tag:'), q.indexOf(' AND (tag:')].filter(function (at) { return at > -1; });
      var cut = cuts.length ? Math.min.apply(null, cuts) : -1;
      text = cut > -1 ? q.slice(0, cut) : q;
      tagPart = cut > -1 ? q.slice(cut + ' AND '.length) : '';
    }
    var tags = tagPart ? tagPart.split(' AND ').map(function (part) {
      var form = part.split(' OR ')[0].replace(/^\(?tag:/, '').replace(/["()]/g, '').trim().toLowerCase();
      return tagValue[form] || form;
    }) : [];
    return {
      ours: ours,
      text: text,
      tags: tags,
      tier: / OR body:/.test(q) ? 2 : 1,
      any: q.indexOf(') OR (') > -1
    };
  }

  // The search page URL of a text, as the search fields send it
  function url(text, opts) {
    var result = build(text, opts);
    var params = new URLSearchParams();
    params.set('q', result.q || String(text || '').trim());
    params.set('type', 'product');
    params.append('filter.v.availability', '1');
    if (result.tags.length) params.set('sp_text', String(text || '').trim());
    if (opts && opts.exact) params.set('sp_exact', '1');
    return readConfig().url + '?' + params.toString();
  }

  /* Typos ------------------------------------------------------------------ */

  // Spanish spellings that sound the same count as no difference
  function sound(word) {
    return norm(word)
      .replace(/h/g, '')
      .replace(/ll/g, 'y')
      .replace(/v/g, 'b')
      .replace(/z/g, 's')
      .replace(/c([ei])/g, 's$1')
      .replace(/g([ei])/g, 'j$1')
      .replace(/qu/g, 'k')
      .replace(/c/g, 'k')
      .replace(/(.)\1+/g, '$1');
  }

  // Letters added, removed, changed or swapped (one swap counts as 1)
  function distance(a, b) {
    var d = [];
    for (var i = 0; i <= a.length; i++) d[i] = [i];
    for (var j = 0; j <= b.length; j++) d[0][j] = j;
    for (i = 1; i <= a.length; i++) {
      for (j = 1; j <= b.length; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
        }
      }
    }
    return d[a.length][b.length];
  }

  // The vocabulary word closest to a word, or null: at most 1 letter of
  // difference up to 5 letters, 2 for longer words; same sound counts as 0;
  // at least 4 letters and no digits; a tie between two words (not forms of
  // the same one) corrects nothing. Words of the vocabulary aren't corrected.
  function closest(word, vocab) {
    var w = norm(word);
    if (w.length < 4 || /[^a-z]/.test(w)) return null;
    var key = stem(w);
    if (vocab.some(function (v) { return stem(v) === key; })) return null;
    var limit = w.length <= 5 ? 1 : 2;
    var best = null;
    var bestKeys = [];
    var bestDistance = limit + 1;
    vocab.forEach(function (v) {
      if (Math.abs(v.length - w.length) > limit + 1) return;
      var d = sound(v) === sound(w) ? 0 : distance(w, v);
      if (d > limit) return;
      if (d < bestDistance) {
        bestDistance = d;
        best = v;
        bestKeys = [stem(v)];
      } else if (d === bestDistance && bestKeys.indexOf(stem(v)) === -1) {
        bestKeys.push(stem(v));
      }
    });
    return bestKeys.length === 1 ? best : null;
  }

  // The text with its typos corrected, or null if nothing changed. Only
  // single words that aren't filter options or ignored words.
  function correct(text, vocab) {
    readConfig();
    vocab = vocab || vocabulary();
    var changed = false;
    var fixed = words(text).map(function (word) {
      if (filterKeys[stem(word)] || stopwords[stem(word)]) return word;
      var better = closest(word, vocab);
      if (!better) return word;
      changed = true;
      return better;
    });
    return changed ? fixed.join(' ') : null;
  }

  /* Vocabulary ------------------------------------------------------------- */

  function storage() {
    try {
      return window.localStorage;
    } catch (e) {
      return null;
    }
  }

  function cachedTitles() {
    if (titleWords) return titleWords;
    var store = storage();
    try {
      var saved = store && JSON.parse(store.getItem(VOCAB_KEY));
      if (saved && saved.time > Date.now() - VOCAB_HOURS * 3600 * 1000 && saved.words) {
        titleWords = saved.words;
      }
    } catch (e) {}
    return titleWords;
  }

  // Tags, types and synonyms, plus the words of the titles if already loaded
  function vocabulary() {
    readConfig();
    var list = baseVocab.slice();
    (cachedTitles() || []).forEach(function (word) { addVocab(list, word); });
    return list;
  }

  // The same with the words of the titles of the available products: 50
  // per page (Liquid's limit), all pages in parallel after the first one.
  // Kept 24 hours in the visitor's browser. If it fails, without them.
  function loadVocabulary() {
    if (cachedTitles()) return Promise.resolve(vocabulary());
    if (titleRequest) return titleRequest;

    function page(n) {
      var u = '/collections/all?filter.v.availability=1&section_id=' + VOCAB_SECTION + '&page=' + n;
      return fetch(u, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var el = doc.getElementById('SearchVocabulary');
          return el ? JSON.parse(el.textContent) : { pages: 0, words: '' };
        });
    }

    titleRequest = page(1)
      .then(function (first) {
        var rest = [];
        for (var n = 2; n <= Math.min(first.pages || 1, 40); n++) rest.push(page(n));
        return Promise.all(rest).then(function (pages) {
          return [first].concat(pages);
        });
      })
      .then(function (pages) {
        var list = [];
        pages.forEach(function (p) {
          String(p.words || '').split(/[^\p{L}]+/u).forEach(function (word) { addVocab(list, word); });
        });
        titleWords = list;
        var store = storage();
        try {
          if (store) store.setItem(VOCAB_KEY, JSON.stringify({ time: Date.now(), words: list }));
        } catch (e) {}
        return vocabulary();
      })
      .catch(function () {
        return vocabulary();
      });
    return titleRequest;
  }

  /* Texts ----------------------------------------------------------------- */

  // A text of the settings with [name] replaced
  function text(key, values) {
    var value = String(readConfig().texts[key] || '');
    Object.keys(values || {}).forEach(function (name) {
      value = value.split('[' + name + ']').join(values[name]);
    });
    return value;
  }

  /* Search fields ---------------------------------------------------------- */

  function isSearchForm(form) {
    if (!form || form.tagName !== 'FORM' || !form.querySelector('input[name="q"], input[data-sq-field]')) return false;
    try {
      return new URL(form.action, location.href).pathname.replace(/\/$/, '') === readConfig().url.replace(/\/$/, '');
    } catch (e) {
      return false;
    }
  }

  function hidden(form, name, value) {
    var input = form.querySelector('input[type="hidden"][name="' + name + '"]');
    if (value == null) {
      if (input) input.remove();
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  // The visible field keeps what the visitor typed and loses its name; a
  // hidden q carries the query. Again after "back" (the page can come back
  // from the cache with the fields as they were).
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!isSearchForm(form)) return;
    var field = form.querySelector('input[data-sq-field]') || form.querySelector('input[name="q"]:not([type="hidden"])');
    if (!field) return;
    var typed = field.value.trim();
    if (!typed) return;
    var result = build(typed, { complete: true });
    if (!result.q) return;
    field.setAttribute('data-sq-field', '');
    field.removeAttribute('name');
    hidden(form, 'q', result.q);
    hidden(form, 'sp_text', result.tags.length ? typed : null);
    if (!form.querySelector('[name="type"]')) hidden(form, 'type', 'product');
    if (!form.querySelector('[name="filter.v.availability"]')) hidden(form, 'filter.v.availability', '1');
  });

  window.SearchQuery = {
    words: words,
    analyze: analyze,
    build: build,
    query: query,
    parse: parse,
    tagTerm: tagTerm,
    url: url,
    correct: correct,
    closest: closest,
    vocabulary: vocabulary,
    loadVocabulary: loadVocabulary,
    text: text,
    config: readConfig
  };
})();
